import {formatDateTime} from "@/utils/filters";
import {
    AddressData,
    AddressImportResult,
    AddressMap,
    AddressSupportStatus,
    BulkImportAction,
    BulkImportCaseAddresses,
    CaseApiCallErrorMessage,
    CaseApiCallErrors,
    CaseDataRaw,
    CaseObjectRaw,
    CurrencyToImportError,
    FailedAddressMap,
    ImportError,
    InvalidAddressMap,
    SourceFiles, SourceFileToInvalidCryptoUnits
} from "@/components/Case/CaseBulkImport/types";
import {ICurrencyData} from "@/components/common/types/currency";
import store from "@/store";
import {CaseDetailViewModel} from "models/CaseModels";
import * as moment from "moment";
import {
    AddressImportError,
    ICurrencyToItems,
    IEntryFormattedForImport,
} from "@/components/common/types/import";
import {handleAddressImport} from "@/components/common/CsvImport/useAddressImport";
import {CancelTokenSource} from "axios";
import {Ref, UnwrapRef} from "vue";

export class BulkImportCase {
    public reportedAt: string;
    public description: string = "";
    public addresses: BulkImportCaseAddresses = {supported: new Map(), unsupported: new Map()}
    public id: number;
    public title: string;
    public isSyncedWithRemote: boolean = false
    public isHidden: boolean = false
    public isWarning: boolean = true
    public sourceFiles: SourceFiles = new Set()
    public invalidAddresses: InvalidAddressMap = new Map<string, SourceFileToInvalidCryptoUnits>()
    public addressImportErrors: CurrencyToImportError = new Map<string, ImportError>()
    public caseApiCallErrors: CaseApiCallErrors = new Map<BulkImportAction, CaseApiCallErrorMessage>()
    public failedAddresses: FailedAddressMap = new Map<string, { sourceFiles: SourceFiles; currencies: Set<string> }>()
    public successfullyImportedAddressCount: number = 0
    public wasCaseJustCreated: boolean = true
    public addressesToImportCount: number = 0
    public shouldHaveAddressesToImport: boolean = false;

    constructor(title: string, reportedAt: string) {
        this.title = title
        this.reportedAt = formatDateTime(reportedAt)
    }


    private setAddressesToImportCount(currencyToItems: ICurrencyToItems) {
        currencyToItems.forEach(entry => this.addressesToImportCount += entry.length)
    }

    private getDataFormattedForImport(categoryName: string): ICurrencyToItems {
        const currencyToItems: ICurrencyToItems = new Map<string, IEntryFormattedForImport[]>()

        const supportedCurrenciesSet = new Set(store.getters.currencies.map((x:ICurrencyData) => x.unit.toUpperCase()));

        // iterate over addresses and assign each to the currencyToItems
        //@ts-ignore
        for (let [address, addressData] of this.addresses.supported) {
            addressData.cryptos.forEach((crypto: string) => {
                crypto = crypto.toUpperCase()
                if (!supportedCurrenciesSet.has(crypto))
                {
                    return
                }

                const entry: IEntryFormattedForImport = {
                    address: address as string,
                    identities: [{label: this.title}],
                    owners: [],
                    categories: [categoryName].filter(c => c)
                }

                if (currencyToItems.has(crypto)) {
                    currencyToItems.get(crypto).push(entry)
                } else {
                    currencyToItems.set(crypto, [entry])
                }
            })
        }

        this.setAddressesToImportCount(currencyToItems)

        return currencyToItems
    }

    public async importAddresses(
        propagateToCluster: boolean,
        propagateToSingletons: boolean,
        categoryName: string,
        cancelToken: Ref<UnwrapRef<CancelTokenSource>>
    ): Promise<AddressImportResult> {
        // format data for import ( currencyToItems )
        const currencyToItems: ICurrencyToItems = this.getDataFormattedForImport(categoryName)

        // Create import callback
        const importRequestCallback = async (value: IEntryFormattedForImport[], key: string): Promise<boolean> =>
            await store.dispatch("importCaseAddressesBasic", {
                body: {
                    items: value,
                    currencyUnit: key,
                    caseId: this.id,
                    propagateToCluster: propagateToCluster,
                    propagateToSingletons: propagateToSingletons
                }, cancelToken: cancelToken
            })

        const {
            totalRequests,
            failedRequestCurrencyToError
        } = await handleAddressImport(currencyToItems, importRequestCallback)

        this.setFailedAddresses(failedRequestCurrencyToError)

        failedRequestCurrencyToError.forEach((error, curr) => {
            this.addressImportErrors.set(curr, {
                errors: error.validationErrors,
                message: error.userFriendlyMessage,
                addresses: currencyToItems.get(curr).map((importEntry: IEntryFormattedForImport) => {
                    return {
                        address: importEntry.address,
                        sourceFiles: this.addresses.supported.get(importEntry.address).sourceFiles
                    }
                })
            })
        })

        this.successfullyImportedAddressCount = this.addressesToImportCount - this.failedAddresses.size

        // log failed currencies
        return {totalRequests, failedRequestCurrencyToError}
    }

    private setFailedAddresses(failedRequestCurrencyToError: Map<string, AddressImportError>) {
        // iterate over this.addresses and assign all addresses that have cryptos in failed currencies to failedAddresses
        this.addresses.supported.forEach((addressData, address) => {
            failedRequestCurrencyToError.forEach((_, failedCurr) => {
                if (addressData.cryptos.has(failedCurr.toLowerCase())) {
                    if (this.failedAddresses.has(address)) {
                        const failedAddress = this.failedAddresses.get(address)
                        addressData.sourceFiles.forEach(file => failedAddress.sourceFiles.add(file))
                        failedAddress.currencies.add(failedCurr)
                    } else {
                        this.failedAddresses.set(address, {
                            sourceFiles: addressData.sourceFiles,
                            currencies: new Set([failedCurr])
                        })
                    }
                }
            })
        })

        // alternatively iterate over failed currencies, create failed addresses
    }

    public updateReportedAt(newReportedAt: string) {
        //overwrite reported_at if datum_zapis is earlier than reported_at
        if (moment(newReportedAt).isBefore(moment(this.reportedAt))) {
            this.reportedAt = formatDateTime(newReportedAt)
        }
    }

    private updateLocal(caseObject: CaseDetailViewModel) {
        // overwrite description with description from DB
        this.description = caseObject.description

        this.wasCaseJustCreated = false
        this.updateReportedAt(caseObject.reported_at)
        this.id = caseObject.id
        this.isHidden = caseObject.is_hidden
        this.isWarning = caseObject.is_warning
    }

    public async updateRemote(cancelToken: Ref<UnwrapRef<CancelTokenSource>>) {
        // edit case request
        const {isSuccess, error}: { isSuccess: boolean, error: any } = await store.dispatch("updateCaseBasic", {
            updatedCase: {
                reported_at: moment(this.reportedAt).unix(),
                title: this.title,
                description: this.description,
                is_hidden: this.isHidden,
                is_warning: this.isWarning,
                id: this.id
            }, cancelToken: cancelToken
        })

        this.isSyncedWithRemote = isSuccess

        if (!isSuccess) {
            this.caseApiCallErrors.set(BulkImportAction.CaseUpdate, error.userFriendlyMessage)
        }

        return isSuccess
    }

    public async ensureCaseExists(
        isHidden: boolean,
        isWarning: boolean,
        cancelToken: Ref<UnwrapRef<CancelTokenSource>>) {
        if (this.id) {
            return;
        }
        const {requestData, error: casesFetchError} = await store.dispatch("getCasesBasic", {
                pagination: {
                    page: 1,
                    itemsPerPage: -1, // all
                    filterTitle: this.title
                }, cancelToken: cancelToken
            }
        )

        if (!requestData) { // Case search resulted in error
            this.caseApiCallErrors.set(BulkImportAction.CaseLookup, casesFetchError.userFriendlyMessage)
            return {caseFound: false, caseCreated: false}
        }

        let searchedCase: CaseDetailViewModel = requestData.data.find((c: CaseDetailViewModel) => c.title === this.title)

        // TODO in v2 - check which addresses to import for each currency

        if (searchedCase) {
            // update case instance
            this.updateLocal(searchedCase)
            return {caseFound: true, caseCreated: false}
        }

        // Create case
        const caseDetail = {
            reported_at: moment(this.reportedAt).unix(),
            title: this.title,
            description: "Case created by bulk import.",
            is_hidden: isHidden,
            is_warning: isWarning,
        }

        const {id, error} = await store.dispatch("createCaseBasic", {caseDetail: caseDetail, cancelToken: cancelToken})

        if (id) {
            this.id = id
            this.isSyncedWithRemote = true
            return {caseFound: false, caseCreated: true}
        }

        // Case Creation Failed
        this.caseApiCallErrors.set(BulkImportAction.CaseCreation, error.userFriendlyMessage)
        return {caseFound: false, caseCreated: false}
    }

    public modifyDescription() {
        const totalAddressCount = this.addresses.supported.size + this.addresses.unsupported.size
        if (totalAddressCount === 0) {
            // nothing to write to description
            return;
        }

        const now = formatDateTime(moment().valueOf())
        const descriptionHeader = `===== Imported at: ${now} =====\n`

        if (!this.description) {
            this.description += descriptionHeader;
        } else {
            this.description += `\n\n${descriptionHeader}`;
        }

        this.getAllAddresses().forEach((addressData, address) => {
            const addressCryptos = Array.from(addressData.cryptos).join(",")
            const addressDocIds = Array.from(addressData.documents).join(",")

            this.description += `${addressDocIds};${address};${addressCryptos}\n`;
        });
    }

    private getAllAddresses = () => {
        const allAddresses: AddressMap = new Map()
        Object.keys(this.addresses).forEach(key => {
            this.addresses[key].forEach((newAddressData: AddressData, newAddress: string) => {
                if (allAddresses.has(newAddress)) {
                    const existingAddressData = allAddresses.get(newAddress)
                    Object.keys(newAddressData).forEach(key => {
                        newAddressData[key].forEach((item: any) => existingAddressData[key].add(item))
                    })
                } else {
                    allAddresses.set(newAddress, newAddressData)
                }
            })
        })
        return allAddresses
    }

    public writeAddresses(caseObjectRaw: CaseObjectRaw): void {

        this.sourceFiles.add(caseObjectRaw.sourceFile)

        if (!caseObjectRaw.data || caseObjectRaw.data.length === 0) {
            return
        }

        const currencyToRegex: Map<string, RegExp> = new Map()

        const currencies = store.getters.currencies
        currencies.forEach((c: ICurrencyData) => {
            currencyToRegex.set(c.unit, new RegExp(c.regexp.slice(1, -1)))
        })

        caseObjectRaw.data.forEach(address => {

            //at least one address has been in the input file
            this.shouldHaveAddressesToImport = true;

            //validate address
            const currenciesToValidate = currencies.filter((curr: ICurrencyData) =>
                address.druh?.some(crypto => crypto.toLowerCase() === curr.unit.toLowerCase()))

            let valid = true
            currenciesToValidate.forEach((curr: ICurrencyData) => {
                if (!currencyToRegex.get(curr.unit)?.test(address.hodnota)) {
                    if (this.invalidAddresses.has(address.hodnota)) {
                        const sourceFileToInvalidCryptoUnits = this.invalidAddresses.get(address.hodnota)

                        if (sourceFileToInvalidCryptoUnits.has(caseObjectRaw.sourceFile))
                        {
                            sourceFileToInvalidCryptoUnits.get(caseObjectRaw.sourceFile).add(curr.unit)
                        } else {
                            sourceFileToInvalidCryptoUnits.set(caseObjectRaw.sourceFile, new Set([curr.unit]))
                        }
                    } else {
                        this.invalidAddresses.set(address.hodnota, new Map([
                            [caseObjectRaw.sourceFile , new Set([curr.unit])]
                        ]))
                    }
                    valid = false;
                }
            })

            if (!valid) {
                return
            }

            this.categorizeAndStoreAddress(address, caseObjectRaw);
        });
    }

    private categorizeAndStoreAddress(address: CaseDataRaw, caseObjectRaw: CaseObjectRaw) {
        const supportedCurrencies = store.getters.currencies.map((curr: ICurrencyData) => curr.unit)

        const isAddressSupported = address.druh.some(crypto => supportedCurrencies.includes(crypto.toUpperCase()))
        const addressType: AddressSupportStatus = isAddressSupported ? AddressSupportStatus.Supported : AddressSupportStatus.Unsupported

        // modify existing entry
        if (this.addresses[addressType].has(address.hodnota)) {
            const addressObject = this.addresses[addressType].get(address.hodnota);
            address.druh.forEach(crypto => addressObject.cryptos.add(crypto));
            addressObject.documents.add(caseObjectRaw.id_dok);
            addressObject.sourceFiles.add(caseObjectRaw.sourceFile);
        } else { // create new entry
            this.addresses[addressType].set(address.hodnota, {
                cryptos: new Set(address.druh),
                sourceFiles: new Set([caseObjectRaw.sourceFile]),
                documents: new Set([caseObjectRaw.id_dok])
            });
        }
    }
}
